/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import * as React from "react";
import {Card} from "../widget/Card";
import {LastUpdated, OnPatientDataUpdatedForMany, PatientsDataModule} from "@modules/PatientDataModule";
import {formatAlertDatetime} from "./alerts/util";
import dayjs from "../../utils/dayjs";
import {FONTS} from "@res/fonts";
import {COLORS} from "@res/colors";
import {ICONS} from "@res/icons";
import {DB_Patient} from "@app/app-shared/patient";
import {ContactsContext, ContactsMap} from "./messages/ContactsContext";
import {OrganizationMap, OrganizationsContext} from "./organizations/OrganizationsContext";
import {UserPreferencesModule} from "@modules/UserPreferencesModule";

type Props = {
    patient: DB_Patient
}

type State = {
    downloading: boolean
}

export class LastInteractionPanel
    extends BaseComponent<Props, State>
    implements LastUpdated, OnPatientDataUpdatedForMany {

    constructor(props: Props) {
        super(props);
        this.state = {downloading: false};
    }

    __lastUpdated = () => this.forceUpdate();
    __onPatientDataUpdatedForMany = () => this.setState({downloading: false});

    private formatTimestamp = (timestamp?: number) => {
        if (!timestamp)
            return "n/a";

        const {date} = formatAlertDatetime(timestamp);
        const time = dayjs(timestamp).format("hh:mm A");
        return date === " Today" ? ` Today at ${time}` : `${date} at ${time}`;

    };

    private formatPlanName = (planName?: string) => {
        if (!planName)
            return "n/a";
        return (planName.charAt(0).toUpperCase() + planName.slice(1)).replace(/_/g, " ");
    };

    private downloadMemberData = (contacts: ContactsMap, orgMap: OrganizationMap) => {
        if (this.state.downloading)
            return;
        const patient = this.props.patient;
        const org = orgMap[patient.organizationId];
        if (!org)
            return;
        this.setState({downloading: true});
        // startTime omitted -> module defaults to the user's saved time filter
        const period = UserPreferencesModule.getTimeFilterSelectedOption();
        PatientsDataModule.getAllPatientDataForMany(contacts, [org], period.value, [patient.unitId]);
    };

    private renderDownloadButton = (contacts: ContactsMap, orgMap: OrganizationMap) => {
        const {downloading} = this.state;
        return <div
            className={"ll_h_c"}
            onClick={() => this.downloadMemberData(contacts, orgMap)}
            style={{
                position: "relative",
                left: "20px",
                top: "10px",
                marginTop: 16,
                width: "fit-content",
                border: `1px solid ${COLORS.neutral_gray_1}`,
                borderRadius: 4,
                padding: "8px 12px",
                cursor: downloading ? "default" : "pointer",
                opacity: downloading ? 0.6 : 1
            }}>
            {ICONS.icon_download(COLORS.grays_dark, 16)}
            {FONTS.reg_16_black(downloading ? "Downloading…" : "Download member data", {marginLeft: 8})}
        </div>;
    };

    render() {
        const selectedPatient = this.props.patient;
        const {timestamp, lastPlan, lastPositivePlanName} = PatientsDataModule.getLastUserAction(selectedPatient.unitId) || {timestamp: 0, lastPlan: "n/a", lastPositivePlanName: undefined};

        return <OrganizationsContext.Consumer>{({organizations: orgMap}) =>
            <ContactsContext.Consumer>{({contacts}) =>
                <div>
                    <Card style={{paddingBottom: "20px"}}>
                        <h1 style={{position: "relative", left: "20px", top: "10px"}}>
                            {FONTS.reg_14_gray(`Last interaction with ElliQ:`)} {FONTS.bold_14_gray_4(this.formatTimestamp(timestamp))}
                        </h1>
                        <h1 style={{position: "relative", left: "20px", top: "10px"}}>
                            {FONTS.reg_14_gray('Last activity:')} {FONTS.bold_14_gray_4(this.formatPlanName(lastPositivePlanName || lastPlan || "n/a"))}
                        </h1>
                        {this.renderDownloadButton(contacts, orgMap)}
                    </Card>
                </div>
            }</ContactsContext.Consumer>
        }</OrganizationsContext.Consumer>;

    }
}
